import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { useState, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Flex, Text } from '@dutchie/common/components/rebass-compat';
import styled from 'styled-components';
import { space } from 'styled-system';

import { ORDER_TYPE_PICKUP, ORDER_TYPE_DELIVERY } from 'shared/constants';
import {
  ResetButton,
  Button,
  CheckoutAccordion,
  CheckoutAccordionSummary,
  CheckoutAccordionActions,
  Tooltip,
} from 'shared/components';
import { DefaultInput, DateInput, PhoneNumberInput } from 'shared/components/text-input-with-label';
import { mediaQueries } from 'shared/styles';

import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import useErnie from 'shared/hooks/use-ernie';
import { getUserSchema } from 'shared/core/yup/validators/user';
import { useUpdateUser } from 'src/state/actions/users';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import StateSelect from 'checkout/components/sections/user/state-select';
import PhotoIdUpload from 'checkout/components/sections/user/photo-id-upload';
import { Typography } from 'src/typography';
import { useUnenrollPayByBankUser } from 'src/payments/hooks';
import {
  getPbbIdentityChangedFields,
  isPbbUnlinkNarrowingEnabled,
  shouldUnenrollForPbbIdentityChanges,
} from 'src/payments/utils/pbb-identity-changed-fields';

function Customer() {
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');
  const [email, setEmail] = useState('');
  const [photoId, setPhotoId] = useState('');
  const [showRewardsTooltip, setShowRewardsTooltip] = useState(false);
  // const [errors, setErrors] = useState([]);
  const { Cart, User } = useStores();
  const Checkout = useCheckout();
  const showErnie = useErnie();
  const updateUser = useUpdateUser();
  const { isEnrolledDutchiePay, handleUnenrollPayByBank } = useUnenrollPayByBankUser();
  const flags = useFlags();
  const useNarrowedPbbUnenrollFields = isPbbUnlinkNarrowingEnabled(flags);

  const { isDelivery, isPickup, order, requiresCustomerState, customerState, isKioskOrder } = Cart;
  const { dispensary, deliveryOption } = order;
  const requirePhotoIdForDelivery = _.get(dispensary, 'requirePhotoIdForDelivery', false);
  const requirePhotoIdForPickup = _.get(dispensary, 'requirePhotoIdForPickup', false);
  const photoIdRequired =
    (requirePhotoIdForDelivery && deliveryOption === 'delivery') ||
    (requirePhotoIdForPickup && deliveryOption === 'pickup');

  const [open, setOpen] = useState(
    (!customerState && requiresCustomerState) || (!User.profile.photoId && photoIdRequired)
  );

  const belowDesktop = window.matchMedia(mediaQueries.desktop);

  const amplitude = useAmplitude();

  // Make sure the card opens if there are any errors
  const hasError =
    Checkout.getError({ card: 'customer' }).error ||
    Checkout.getError({ path: 'profile.firstName' }).error ||
    Checkout.getError({ path: 'profile.lastName' }).error ||
    Checkout.getError({ path: 'profile.phone' }).error ||
    Checkout.getError({ path: 'profile.birthday' }).error ||
    Checkout.getError({ path: 'profile.email' }).error ||
    Checkout.getError({ path: 'profile.photoId' }).error ||
    Checkout.getError({ path: 'profile.state' }).error;

  useEffect(() => {
    if (hasError) {
      setOpen(true);
    }
  }, [hasError]);

  // when the tooltip is shown, set a timeout to clear it
  // if the tooltip is hidden before the timeout ends, clear it
  useEffect(() => {
    let timeout = null;
    if (showRewardsTooltip) {
      timeout = setTimeout(() => setShowRewardsTooltip(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [showRewardsTooltip]);

  useEffect(() => {
    setShowRewardsTooltip(Cart.hasRewardsWallet);
  }, [Cart.hasRewardsWallet]);

  function resetFields() {
    // setErrors([]);
    setFirstName(User.profile.firstName);
    setLastName(User.profile.lastName);
    setPhone(User.profile.phone);
    setPhotoId(User.profile.photoId);
    setBirthday(User.formatedBirthdate);
    setEmail(User.email);
  }

  async function updatePhone({ target }) {
    const { value = '' } = target || {};
    setPhone(value);
    // not automatically cleared like guest phone is
    Cart.clearRewards();
  }

  function onCancel() {
    resetFields();
    setOpen(!open);
    Checkout.removeError({ path: 'saveOrCancel' });
  }

  useEffect(() => {
    resetFields();
    Cart.order.isGuestOrder = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // validateSubmit currently checks this flag to force the user
    // to save before submitting
    Checkout.customerCardOpen = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function isUserValid() {
    const userToValidate = {
      _id: User.id,
      emails: [{ address: email }],
      profile: {
        birthday,
        firstName,
        lastName,
        phone,
        photoId,
      },
    };
    try {
      const orderType = isPickup ? ORDER_TYPE_PICKUP : ORDER_TYPE_DELIVERY;
      const userSchema = getUserSchema({
        orderType,
        requirePhotoIdForDelivery,
        requirePhotoIdForPickup,
        isKioskOrder,
        userId: User._id,
      });
      await userSchema.validate(userToValidate, { abortEarly: false });
      return { valid: true };
    } catch (err) {
      Checkout.addErrors(err.inner);
      return { valid: false, err };
    }
  }

  async function saveUser() {
    const { valid } = await isUserValid();
    if (!valid) {
      return;
    }
    amplitude.log(AmplitudeEvents.checkout.clickSave, {
      componentName: `customer`,
      dispensaryId: dispensary?.id,
      dispensaryName: dispensary?.name,
    });
    setLoading(true);

    const changedFields = getPbbIdentityChangedFields(
      { firstName, lastName, phone, birthday, email },
      {
        firstName: User.profile.firstName,
        lastName: User.profile.lastName,
        phone: User.profile.phone,
        birthday: User.formatedBirthdate,
        email: User.email,
      }
    );
    if (isEnrolledDutchiePay && shouldUnenrollForPbbIdentityChanges(changedFields, useNarrowedPbbUnenrollFields)) {
      const { success } = await handleUnenrollPayByBank();
      if (!success) {
        setLoading(false);
        showErnie('We were unable to save your information. Please try again later.', 'danger');
        return;
      }
    }

    User.profile.firstName = firstName;
    User.profile.lastName = lastName;
    User.profile.phone = phone;
    User.profile.birthday = birthday;
    User.profile.photoId = photoId;
    User.email = email;

    const { success, error } = await updateUser(User);

    if (success === true) {
      showErnie('User information updated.', 'success');
      setOpen(false);
      Checkout.removeError({ match: 'customer' });
    } else {
      showErnie('We were unable to save your information. Please try again later.', 'danger');
      console.error(error);
    }

    setLoading(false);
  }

  return (
    <CheckoutLabelGrouping id='customer' data-cy='customer-card' data-test='customer-card' data-testid='customer-card'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Customer</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        <CheckoutAccordion
          defaultButton={false}
          defaultCancel={false}
          error={Checkout.getError({ card: 'customer' }).error}
          expandText='Edit'
          expanded={open}
          onExpand={() => setOpen(!open)}
        >
          <CheckoutAccordionSummary data-private>
            {!open && (
              <SummaryText fontSize={13} color='#5d666d' mt='1px'>
                Logged in as <span>{User.email}</span>
              </SummaryText>
            )}

            {open && (
              <FieldContainer justifyContent='space-between'>
                <Text color='#5d666d' fontSize={13}>
                  Edit your info below
                </Text>
                <Flex>
                  <HeaderButton
                    onClick={() => {
                      User.logout('/checkout');
                      Checkout.customerCardOpen = false;
                    }}
                    data-cy='logout-button'
                    data-test='logout-button'
                    data-testid='logout-button'
                  >
                    Logout
                  </HeaderButton>
                  <HeaderDivider>{' | '}</HeaderDivider>
                  <HeaderButton
                    onClick={onCancel}
                    data-cy='cancel-button'
                    data-test='cancel-button'
                    data-testid='cancel-button'
                  >
                    Cancel
                  </HeaderButton>
                </Flex>
              </FieldContainer>
            )}
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <FieldContainer mt='8px' mb='17px'>
              <NameContainer>
                <DefaultInput
                  onValueAfterError={() => Checkout.removeError({ path: 'profile.firstName' })}
                  error={Checkout.getError({ path: 'profile.firstName' }).error}
                  errorMessage={Checkout.getError({ path: 'profile.firstName' }).message}
                  label='First Name'
                  onChange={(e) => setFirstName(e.target.value)}
                  value={firstName}
                  width='100%'
                  mr='14px'
                  data-cy='first-name-input'
                  data-test='first-name-input'
                  autoComplete='given-name'
                />
                <DefaultInput
                  onValueAfterError={() => Checkout.removeError({ path: 'profile.lastName' })}
                  error={Checkout.getError({ path: 'profile.lastName' }).error}
                  errorMessage={Checkout.getError({ path: 'profile.lastName' }).message}
                  label='Last Name'
                  onChange={(e) => setLastName(e.target.value)}
                  value={lastName}
                  width='100%'
                  data-cy='last-name-input'
                  data-test='last-name-input'
                  autoComplete='family-name'
                />
              </NameContainer>
            </FieldContainer>

            <FieldContainer mb='19px'>
              <PhoneNumberInput
                onValueAfterError={() => Checkout.removeError({ path: 'profile.phone' })}
                error={Checkout.getError({ path: 'profile.phone' }).error}
                errorMessage={Checkout.getError({ path: 'profile.phone' }).message}
                width='100%'
                label='Mobile Phone'
                value={phone}
                onChange={updatePhone}
                autoComplete='tel'
                data-test='phone-input'
              />
              <Tooltip
                hideIcon
                leftArrow={!belowDesktop}
                arrowRight={belowDesktop && '255px'}
                visible={showRewardsTooltip}
                bottom={belowDesktop ? '58px' : '-10px'}
                right={belowDesktop ? '37px' : 'auto'}
                mobilePosition={{ right: '-14px' }}
                left={belowDesktop ? 'auto' : '15px'}
                width={belowDesktop ? '309px' : '309px'}
                tooltipCopy={
                  <>
                    You’re connected to <Bold>{Cart.rewardsProgramDisplayName}</Bold>! Scroll down to view your wallet
                    for this order.
                  </>
                }
              />
            </FieldContainer>

            <FieldContainer mb='18px'>
              <DateInput
                onValueAfterError={() => Checkout.removeError({ path: 'profile.birthday' })}
                error={Checkout.getError({ path: 'profile.birthday' }).error}
                errorMessage={Checkout.getError({ path: 'profile.birthday' }).message}
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                width='100%'
                label='Birthdate'
                autoComplete='bday'
                data-test='birthday-input'
              />
            </FieldContainer>

            <FieldContainer>
              <DefaultInput
                onValueAfterError={() => Checkout.removeError({ path: 'profile.email' })}
                error={Checkout.getError({ path: 'profile.email' }).error}
                errorMessage={Checkout.getError({ path: 'profile.email' }).message}
                label='Email'
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                width='100%'
                autoComplete='email'
                disabled={User.isSsoUser}
                title={User.isSsoUser && 'Email address cannot be changed when logged in with a social account.'}
                data-test='email-input'
              />
            </FieldContainer>

            {User.isSsoUser && (
              <EmailDisabledText size='xsmall'>
                Email address cannot be changed when logged in with a social account.
              </EmailDisabledText>
            )}

            {((isDelivery && requirePhotoIdForDelivery) || (isPickup && requirePhotoIdForPickup)) && (
              <FieldContainer>
                <PhotoIdUpload
                  id='customerPhoto'
                  onValueAfterError={() => Checkout.removeError({ path: 'profile.photoId' })}
                  error={Checkout.getError({ path: 'profile.photoId' }).error}
                  onPhotoUploaded={(photo) => setPhotoId(photo)}
                  onPhotoReset={() => setPhotoId('')}
                  value={photoId}
                />
              </FieldContainer>
            )}
            {requiresCustomerState && (
              <FieldContainer>
                <StateSelect error={Checkout.getError({ path: 'profile.state' }).error} />
              </FieldContainer>
            )}
            <StyledButton
              width='73px'
              height='33px'
              onClick={saveUser}
              loading={loading}
              data-cy='save-button'
              data-test='save-button'
            >
              Save
            </StyledButton>
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(Customer);

const Bold = styled.span`
  font-weight: bold;
`;

export const SummaryText = styled(Text)`
  span {
    font-weight: bold;
  }
`;

const HeaderDivider = styled.span`
  color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme.customized.colors.buttonsLinks});
  font-size: 13px;
  margin: 0 7px;
`;

const HeaderButton = styled(ResetButton)`
  color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme.customized.colors.buttonsLinks}) !important;
  font-size: 13px;
  line-height: 13px;
  &:hover {
    text-decoration: underline;
  }
`;

const FieldContainer = styled(Flex)`
  width: 100%;
  margin-bottom: 14px;
  ${space};

  @media ${mediaQueries.phone} {
    flex-wrap: wrap;
  }

  &:last-of-type {
    margin-bottom: 0px;
    padding-top: 1px;
  }
`;

const NameContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
  margin-top: 26px;
`;

const EmailDisabledText = styled(Typography.Label)`
  color: ${({ theme }) => theme.colors.grey[45]};
  margin-top: 12px;
`;
